import React, { useState } from 'react';
import { AlertCircleIcon, ArrowLeftIcon, CheckCircleIcon, ClockIcon, CreditCardIcon, DollarSignIcon, FileTextIcon, MessageSquareIcon, PrinterIcon, TrendingUpIcon } from 'lucide-react';
export function InvoiceDetail({
  invoice,
  onBack,
  onAddComment
}) {
  const defaultData = {
    id: 'INV-2023-001',
    date: '2023-10-15',
    dueDate: '2023-11-15',
    amount: 1250.75,
    status: 'Pending Approval',
    poNumber: 'PO-2023-001',
    description: 'Office Supplies',
    items: [{
      id: 1,
      description: 'Desk Chair',
      quantity: 2,
      unitPrice: 250.5,
      total: 501.0
    }, {
      id: 2,
      description: 'Filing Cabinet',
      quantity: 1,
      unitPrice: 349.75,
      total: 349.75
    }, {
      id: 3,
      description: 'Desk Lamp',
      quantity: 5,
      unitPrice: 80.0,
      total: 400.0
    }],
    payments: [{
      id: 'PAY-2023-001',
      referenceNumber: 'REF12345',
      date: '2023-11-01',
      amount: 750.0,
      status: 'Cleared',
      method: 'Bank Transfer',
      notes: 'First payment installment'
    }, {
      id: 'PAY-2023-002',
      referenceNumber: 'REF12346',
      date: '2023-11-10',
      amount: 250.0,
      status: 'Pending',
      method: 'Credit Card',
      notes: 'Second payment installment'
    }],
    amountPaid: 750.0,
    remainingBalance: 500.75,
    paymentStatus: 'Partially Paid',
    comments: [{
      id: 1,
      user: 'System',
      date: '2023-10-15 09:30',
      text: 'Invoice submitted successfully.'
    }, {
      id: 2,
      user: 'John (AP Manager)',
      date: '2023-10-16 14:20',
      text: 'Invoice received and under review.'
    }],
    history: [{
      date: '2023-10-15 09:30',
      action: 'Invoice Submitted',
      user: 'Vendor Portal'
    }, {
      date: '2023-10-15 10:15',
      action: 'Invoice Received',
      user: 'System'
    }, {
      date: '2023-10-16 14:20',
      action: 'Review Started',
      user: 'John (AP Manager)'
    }, {
      date: '2023-11-01 11:30',
      action: 'Partial Payment Received',
      user: 'System'
    }]
  };
  const [invoiceData, setInvoiceData] = useState(() => {
    // More robust initialization that checks for required properties
    return invoice && invoice.comments ? invoice : defaultData;
  });
  const [newComment, setNewComment] = useState('');
  const getStatusIcon = status => {
    switch (status) {
      case 'Pending Approval':
        return <ClockIcon className="h-6 w-6 text-yellow-500" data-id="element-480" />;
      case 'Approved':
        return <CheckCircleIcon className="h-6 w-6 text-green-500" data-id="element-481" />;
      case 'Paid':
      case 'Fully Paid':
        return <TrendingUpIcon className="h-6 w-6 text-blue-500" data-id="element-482" />;
      case 'Partially Paid':
        return <DollarSignIcon className="h-6 w-6 text-blue-400" data-id="element-483" />;
      case 'Rejected':
        return <AlertCircleIcon className="h-6 w-6 text-red-500" data-id="element-484" />;
      default:
        return <FileTextIcon className="h-6 w-6 text-gray-500" data-id="element-485" />;
    }
  };
  const getStatusColor = status => {
    switch (status) {
      case 'Pending Approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Paid':
      case 'Fully Paid':
        return 'bg-blue-100 text-blue-800';
      case 'Partially Paid':
        return 'bg-indigo-100 text-indigo-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getPaymentStatusIcon = status => {
    switch (status) {
      case 'Cleared':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-486" />;
      case 'Pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-487" />;
      case 'Failed':
        return <AlertCircleIcon className="h-5 w-5 text-red-500" data-id="element-488" />;
      case 'Cancelled':
        return <XIcon className="h-5 w-5 text-gray-500" data-id="element-489" />;
      default:
        return <CreditCardIcon className="h-5 w-5 text-gray-500" data-id="element-490" />;
    }
  };
  const getPaymentMethodIcon = method => {
    switch (method) {
      case 'Bank Transfer':
        return <TrendingUpIcon className="h-5 w-5 text-blue-500" data-id="element-491" />;
      case 'Cheque':
        return <FileTextIcon className="h-5 w-5 text-gray-500" data-id="element-492" />;
      case 'Credit Card':
        return <CreditCardIcon className="h-5 w-5 text-purple-500" data-id="element-493" />;
      case 'BPAY':
        return <DollarSignIcon className="h-5 w-5 text-green-500" data-id="element-494" />;
      default:
        return <CreditCardIcon className="h-5 w-5 text-gray-500" data-id="element-495" />;
    }
  };
  const handleAddComment = e => {
    e.preventDefault();
    if (!newComment.trim()) return;
    // Use the onAddComment function passed from VendorPortal
    if (onAddComment) {
      const updatedInvoice = onAddComment(invoiceData, newComment);
      setInvoiceData(updatedInvoice);
    } else {
      // Fallback to local state update if onAddComment is not provided
      setInvoiceData(prevData => ({
        ...prevData,
        comments: [...(prevData.comments || []), {
          id: (prevData.comments?.length || 0) + 1,
          user: 'Vendor',
          date: new Date().toLocaleString(),
          text: newComment
        }]
      }));
    }
    setNewComment('');
  };
  return <div data-id="element-496">
      <div className="mb-6" data-id="element-497">
        <button onClick={onBack} className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-900" data-id="element-498">
          <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-499" />
          Back to Invoices
        </button>
      </div>
      <div className="mb-6 flex justify-between items-start" data-id="element-500">
        <div data-id="element-501">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center" data-id="element-502">
            Invoice {invoiceData.id}
            <span className={`ml-3 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoiceData.paymentStatus || invoiceData.status)}`} data-id="element-503">
              {invoiceData.paymentStatus || invoiceData.status}
            </span>
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-504">
            Submitted on {invoiceData.date}
          </p>
        </div>
        <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-505">
          <PrinterIcon className="h-4 w-4 mr-2" data-id="element-506" />
          Print Invoice
        </button>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-507">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-508">
          <div data-id="element-509">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-510">
              Invoice Details
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-511">
              PO: {invoiceData.poNumber}
            </p>
          </div>
          <div className="flex items-center" data-id="element-512">
            {getStatusIcon(invoiceData.paymentStatus || invoiceData.status)}
            <span className="ml-2 text-sm font-medium text-gray-900" data-id="element-513">
              {invoiceData.paymentStatus || invoiceData.status}
            </span>
          </div>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-514">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2" data-id="element-515">
            <div className="sm:col-span-1" data-id="element-516">
              <dt className="text-sm font-medium text-gray-500" data-id="element-517">
                Invoice Date
              </dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-518">{invoiceData.date}</dd>
            </div>
            <div className="sm:col-span-1" data-id="element-519">
              <dt className="text-sm font-medium text-gray-500" data-id="element-520">Due Date</dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-521">
                {invoiceData.dueDate}
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-522">
              <dt className="text-sm font-medium text-gray-500" data-id="element-523">
                Purchase Order
              </dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-524">
                {invoiceData.poNumber}
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-525">
              <dt className="text-sm font-medium text-gray-500" data-id="element-526">
                Total Amount
              </dt>
              <dd className="mt-1 text-sm text-gray-900 font-medium" data-id="element-527">
                ${invoiceData.amount.toFixed(2)}
              </dd>
            </div>
            {invoiceData.paymentStatus === 'Partially Paid' && <>
                <div className="sm:col-span-1" data-id="element-528">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-529">
                    Amount Paid
                  </dt>
                  <dd className="mt-1 text-sm text-green-600 font-medium" data-id="element-530">
                    ${invoiceData.amountPaid.toFixed(2)}
                  </dd>
                </div>
                <div className="sm:col-span-1" data-id="element-531">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-532">
                    Remaining Balance
                  </dt>
                  <dd className="mt-1 text-sm text-indigo-600 font-medium" data-id="element-533">
                    ${invoiceData.remainingBalance.toFixed(2)}
                  </dd>
                </div>
              </>}
            <div className="sm:col-span-2" data-id="element-534">
              <dt className="text-sm font-medium text-gray-500" data-id="element-535">Description</dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-536">
                {invoiceData.description}
              </dd>
            </div>
          </dl>
        </div>
      </div>
      {/* Payment Information Section */}
      {invoiceData.payments && invoiceData.payments.length > 0 && <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-537">
          <div className="px-4 py-5 sm:px-6" data-id="element-538">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-539">
              Payment Information
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-540">
              Details of payments made against this invoice
            </p>
          </div>
          <div className="border-t border-gray-200" data-id="element-541">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-542">
              <thead className="bg-gray-50" data-id="element-543">
                <tr data-id="element-544">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-545">
                    Reference
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-546">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-547">
                    Method
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-548">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-549">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-550">
                {invoiceData.payments.map(payment => <tr key={payment.id} data-id="element-551">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-552">
                      {payment.referenceNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-553">
                      {payment.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-554">
                      <div className="flex items-center" data-id="element-555">
                        {getPaymentMethodIcon(payment.method)}
                        <span className="ml-1" data-id="element-556">{payment.method}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-557">
                      <div className="flex items-center" data-id="element-558">
                        {getPaymentStatusIcon(payment.status)}
                        <span className={`ml-1 text-sm ${payment.status === 'Cleared' ? 'text-green-600' : payment.status === 'Pending' ? 'text-yellow-600' : payment.status === 'Failed' ? 'text-red-600' : 'text-gray-500'}`} data-id="element-559">
                          {payment.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right" data-id="element-560">
                      ${payment.amount.toFixed(2)}
                    </td>
                  </tr>)}
                {invoiceData.paymentStatus === 'Partially Paid' && <tr className="bg-gray-50" data-id="element-561">
                    <td colSpan={4} className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right" data-id="element-562">
                      Remaining Balance
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600 text-right" data-id="element-563">
                      ${invoiceData.remainingBalance.toFixed(2)}
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-564">
        <div className="px-4 py-5 sm:px-6" data-id="element-565">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-566">
            Line Items
          </h3>
        </div>
        <div className="border-t border-gray-200" data-id="element-567">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-568">
            <thead className="bg-gray-50" data-id="element-569">
              <tr data-id="element-570">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-571">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-572">
                  Quantity
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-573">
                  Unit Price
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-574">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-575">
              {invoiceData.items.map(item => <tr key={item.id} data-id="element-576">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-577">
                    {item.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-578">
                    {item.quantity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-579">
                    ${item.unitPrice.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right" data-id="element-580">
                    ${item.total.toFixed(2)}
                  </td>
                </tr>)}
              <tr className="bg-gray-50" data-id="element-581">
                <td colSpan={3} className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right" data-id="element-582">
                  Total
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right" data-id="element-583">
                  ${invoiceData.amount.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-584">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-585">
          <div className="px-4 py-5 sm:px-6 flex items-center" data-id="element-586">
            <MessageSquareIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-587" />
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-588">
              Comments
            </h3>
            <span className="ml-2 text-xs text-indigo-600" data-id="element-589">
              (Comments are also visible in Communications)
            </span>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-590">
            <div className="space-y-4 max-h-80 overflow-y-auto" data-id="element-591">
              {invoiceData?.comments?.map(comment => <div key={comment.id} className="bg-gray-50 p-3 rounded-lg" data-id="element-592">
                  <div className="flex justify-between text-xs text-gray-500 mb-1" data-id="element-593">
                    <span className="font-medium" data-id="element-594">{comment.user}</span>
                    <span data-id="element-595">{comment.date}</span>
                  </div>
                  <p className="text-sm text-gray-800" data-id="element-596">{comment.text}</p>
                </div>) || <p className="text-sm text-gray-500" data-id="element-597">No comments yet.</p>}
            </div>
            <form onSubmit={handleAddComment} className="mt-4" data-id="element-598">
              <div data-id="element-599">
                <label htmlFor="comment" className="sr-only" data-id="element-600">
                  Add a comment
                </label>
                <textarea id="comment" name="comment" rows={3} className="shadow-sm block w-full focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border border-gray-300 rounded-md" placeholder="Add a comment..." value={newComment} onChange={e => setNewComment(e.target.value)} data-id="element-601"></textarea>
              </div>
              <div className="mt-3 flex justify-end" data-id="element-602">
                <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-603">
                  Add Comment
                </button>
              </div>
            </form>
          </div>
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-604">
          <div className="px-4 py-5 sm:px-6" data-id="element-605">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-606">
              Invoice History
            </h3>
          </div>
          <div className="border-t border-gray-200" data-id="element-607">
            <ul className="divide-y divide-gray-200" data-id="element-608">
              {invoiceData.history.map((event, idx) => <li key={idx} className="px-4 py-4 sm:px-6" data-id="element-609">
                  <div className="flex items-center justify-between" data-id="element-610">
                    <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-611">
                      {event.action}
                    </p>
                    <div className="ml-2 flex-shrink-0 flex" data-id="element-612">
                      <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800" data-id="element-613">
                        {event.date}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between" data-id="element-614">
                    <div className="sm:flex" data-id="element-615">
                      <p className="flex items-center text-sm text-gray-500" data-id="element-616">
                        {event.user}
                      </p>
                    </div>
                  </div>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </div>;
}